import {IRoutingService} from './routing.service.interface';
import {ServiceBase} from '../ServiceBase';
import {action, observable} from 'mobx';
import {IRoute} from './route/route.interface';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {Uri} from '../location/uri/uri';
import {OrbitRoute} from './route/orbit-route';
import {FallbackRoute} from './route/fallback-route';
import {UriPath} from '../location/uri-path/uri-path';
import {tryMatchPath} from './routing-utils';
import {Check} from "@sparkware/app-utils";
import {RouteDefinition} from "../routing-configuration/route-definition/route-definition";
import {FEATURE_ROUTE_OVERWRITE} from "../../../common/const/ab-testing-names";

//https://reacttraining.com/react-router/web/api/match

export class RoutingService extends ServiceBase implements IRoutingService {

  constructor(services: IServiceFactoryExtended) {
    super(services);

    this.reaction(() => this.services.location.currentUri,
      (uri) => this._updateCurrentRoute(uri),
      {
        fireImmediately: true
      });

    this.reaction(
        () => this.services.user.isAuthorized,
        (isAuthorized) => {
          if(isAuthorized) {

            let featureData = this.services.sdk.getAbTestingDataForFeature(FEATURE_ROUTE_OVERWRITE);

            if(Check.isNullOrUndefined(featureData)){
              let abChangeRoute = this.services.storage.getJson('orbit_AbRouteOverwrite');
              if(!Check.isNullOrUndefined(abChangeRoute)) {
                this.services.storage.removeItem('orbit_AbRouteOverwrite');
              }
            }

            let routeChangeData = featureData && featureData.Properties && featureData.Properties.routesChange;

            if(!Check.isNullOrUndefined(routeChangeData)) {
              this.services.storage.setItem('orbit_AbRouteOverwrite', routeChangeData);
            }
          }

          this._doRedirect(this.currentRoute?.routeDefinition);
        }
    );
  }

  @observable.ref currentRoute: IRoute;
  private _currentPathName: UriPath = null;

  @action.bound
  private _updateCurrentRoute(uri: Uri): void {
    if(this._currentPathName?.equals(uri.pathName)) {
      return;
    }

    let routingConfiguration = this.services.routingConfiguration.getRoutingConfiguration();

    let match = tryMatchPath(uri.pathName.toString(), routingConfiguration.routes);

    if(match) {
      let didRedirect = this._doRedirect(match.routeDefinition);
      if(didRedirect) {
        return;
      }
      this._removeCurrentRouteCssSelectors();
      this.currentRoute = new OrbitRoute(this._overrideArenaType(match.routeDefinition), match.routeParams);
      this._addCurrentRouteCssSelectors();
    } else {
      if (this.services.miniOrbit.isEnabled) {
          this.currentRoute = new FallbackRoute(this.services.miniOrbit.fallbackRouteRender);
      } else {
          this.currentRoute = new FallbackRoute(routingConfiguration.fallbackRoute.render);
      }
    }

    this._currentPathName = uri.pathName;

    this.services.window.scrollTo(0, 0); //we want that when a new page is shown to start from the top
    this.services.sdk.closeCurrentDetailHistory();
  }

  private _overrideArenaType(routeDefinition: RouteDefinition): RouteDefinition {
    try {
      let routesToArenaTypesMapping = this.services.configuration.data.arenasRoutingOverride?.routesToArenaTypesMapping;
      if(routesToArenaTypesMapping){
        for(const path of routeDefinition.paths){
          if(routesToArenaTypesMapping[path]){
            routeDefinition.data.arenaType = routesToArenaTypesMapping[path];
            return routeDefinition;
          }
        }
      }
      return routeDefinition;
    }catch (e) {
     this.services.logger.error('Error while overriding arenaType in routing service!', e);
     return routeDefinition;
    }
  }

  private _removeCurrentRouteCssSelectors() {
      this._getCurrentRouteCssSelectors().forEach(selector => {
          this.services.dom.body.classList.remove(selector);
      });
  }

  private _addCurrentRouteCssSelectors() {
    this._getCurrentRouteCssSelectors().forEach(selector => {
        this.services.dom.body.classList.add(selector);
    });
  }

  private _getCurrentRouteCssSelectors(): string[] {
      if(!this.currentRoute?.routeDefinition?.cssSelectors) {
        return [];
      }

      return this.currentRoute.routeDefinition.cssSelectors.split(' ').filter(selector => selector);

  }

  isOrbitRoute(path: UriPath): boolean {
    let match = tryMatchPath(path.toString(), this.services.routingConfiguration.getRoutingConfiguration().routes);
    if(match) {
      return true;
    } else {
      return false;
    }
  }

  private _cleanUpOldAbTestData(): void {
    let abChangeRoute = this.services.storage.getJson('orbit_abChangeRoute');
    if(!Check.isNullOrUndefined(abChangeRoute)) {
      // This cleanup is done to delete the local storage data remaining from the previous AB Test
      // TODO: Remove this after some time
      this.services.storage.removeItem('orbit_abChangeRoute');
    }
  }

  _doRedirect(routeDefinition: RouteDefinition): boolean {
    try {

      this._cleanUpOldAbTestData();

      let abChangeRoute = this.services.storage.getJson('orbit_AbRouteOverwrite');

      for(let index=0; index < routeDefinition.paths?.length; index++) {
        let currentPathName = routeDefinition.paths[index]?.toLowerCase();
        if(abChangeRoute && abChangeRoute[currentPathName]) {
          this.services.location.push(abChangeRoute[currentPathName]);
          return true;
        }
      }
      return false;
    } catch(err) {
      this.services.logger.error('Routing service: doRedirect failed. Error: ' + err);
      return false;
    }
  }
}
